import { type ReactNode } from 'react';
import { styled } from '@mui/material/styles';

import Icon from '../../components/Icon';
import { notchMask } from '../../components/notch';
import { TAG_COLORS } from '../../shared/icons.generated';
import { BRAND } from '../../shared/theme/paletteDark';
import { FONTS } from '../../shared/theme/typography';
import { formatArea, formatTimeRange, timeSlotOf } from '../../shared/taxonomy';
import { localDate, venueText, type Opportunity } from '../../shared/opportunity';
import { Badge, Badges, Chip, accessBadges, moreDatesLabel, shortDate } from './cardFacts';
import { withAlpha } from '../../shared/utils/color';

/**
 * The phone's ticket: one event, drawn.
 *
 * A ticket with no stub — the title beside the event's intent icons, the time
 * and the neighborhood on a line each, then the summary. What kind of event it
 * is and how to get in (type, format, RSVP) is one tap away in the callout. The
 * topics are sticky notes on the ticket's right edge, icon only, half on the
 * ticket and half off it.
 *
 * Lifted out of `MobileEventCard` on Sept 19 so a shared schedule draws its
 * events the same way the app does (Sam): that file keeps the push gesture, the
 * band and the label, and this one is the ticket itself, with no behaviour of
 * its own. A page that wants a control on the ticket — the shared page's "Add
 * to my schedule" — passes it as `action`.
 */

/** A tag note is twice as wide as it is tall, and half of it hangs off the ticket. */
export const NOTE_H = 24;
export const NOTE_W = NOTE_H * 2;
/** Between notes, and between the notes and the screen edge. */
export const NOTE_GAP = 5;
/** The first note's top, from the ticket's top edge. */
const NOTES_TOP = 97;
/** Ticket below the last note: a short card grows rather than let a note hang off it. */
const NOTES_FOOT = 12;
const MAX_NOTES = 3;
const MAX_INTENTS = 2;

/**
 * Screen edge to ticket, the same on both sides so the ticket is centred on the
 * screen: the half of a note that overhangs, and the gap beyond it. The list is
 * inset by this, and its rows of cards reach back out to the screen's edges.
 */
export const PHONE_GUTTER = NOTE_W / 2 + NOTE_GAP;

export const PAD = 14;
/** Text stops this far from the ticket's right edge, clear of the notes. */
export const TEXT_CLEAR = NOTE_W / 2 + 16 - PAD;

const SAVED_FILL = '#c4beb8';
const INTENT_COLOR = '#cdc7c1';
/** The intent icons on a saved card, darkened with it so they stay visible. */
const INTENT_ON_SAVED = '#a8a29c';
const SCHEDULED_MARK = '#4a632c';
const SAVED_MARK = '#47355b';

/* ── Parts ──────────────────────────────────────────────────────────────── */

/** The notes hang off the ticket, so they share a positioned box with it. */
const Frame = styled('div')({
  position: 'relative',
});

const Card = styled('article', { shouldForwardProp: (p) => p !== '$saved' })<{ $saved: boolean }>(
  ({ $saved }) => ({
    position: 'relative',
    padding: PAD,
    background: $saved ? SAVED_FILL : BRAND.parchment,
    color: BRAND.mocha,
    ...notchMask({ radius: 12 }),
  }),
);

const Title = styled('h3')({
  margin: 0,
  // The left three quarters of the ticket; the intent icons have the rest.
  width: `calc(75% + ${PAD / 2}px)`,
  fontFamily: FONTS.serif,
  fontWeight: 700,
  fontSize: 18,
  lineHeight: 1.25,
  letterSpacing: '-0.01em',
  display: '-webkit-box',
  WebkitLineClamp: 3,
  WebkitBoxOrient: 'vertical',
  overflow: 'hidden',
});

/** The title as a link, where the ticket itself is not the way in. */
const TitleLink = styled('a')({
  color: 'inherit',
  textDecoration: 'none',
  '&:hover': { textDecoration: 'underline', textUnderlineOffset: 3 },
});

/** Time, then neighborhood: an icon column and a text column. */
const Facts = styled('div')({
  display: 'grid',
  gridTemplateColumns: '18px minmax(0, 1fr)',
  alignItems: 'center',
  columnGap: 3,
  margin: '11px 0 0',
  paddingRight: TEXT_CLEAR,
  fontFamily: FONTS.body,
  fontSize: 12,
  fontWeight: 700,
  letterSpacing: '0.01em',
  lineHeight: '23px',
  whiteSpace: 'nowrap',
});

const FactIcon = styled('span')({
  display: 'flex',
  justifyContent: 'center',
});

/**
 * The time keeps its whole self; anything beside it gives way.
 *
 * A time cut in half is a wrong time — "5:00 PM - 7:0" reads as a bug — while
 * a neighborhood cut short is still a neighborhood, and the card is a summary
 * whose full text is one tap away.
 */
const FactText = styled('span', { shouldForwardProp: (p) => p !== '$plain' })<{ $plain?: boolean }>(
  ({ $plain }) => ({
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    minWidth: 0,
    overflow: 'hidden',
    '& > span:first-of-type': { flexShrink: 0 },
    '& > span:only-child': { flexShrink: 1, overflow: 'hidden', textOverflow: 'ellipsis' },
    // A location with no borough ("Online", "Multiple venues") reads as the summary does.
    ...($plain && { fontWeight: 400, letterSpacing: 'normal' }),
  }),
);

const Summary = styled('p')({
  margin: '6px 0 0',
  paddingRight: TEXT_CLEAR,
  fontFamily: FONTS.body,
  fontSize: 12,
  lineHeight: 1.3,
  display: '-webkit-box',
  WebkitLineClamp: 3,
  WebkitBoxOrient: 'vertical',
  overflow: 'hidden',
});

/** Whatever a page puts on the ticket, clear of the notes. */
const Action = styled('div')({
  display: 'flex',
  justifyContent: 'flex-end',
  marginTop: 12,
  paddingRight: TEXT_CLEAR,
});

/** Centred 45px in from the ticket's right edge, beside the title. */
const Intents = styled('div')({
  position: 'absolute',
  top: 17,
  right: 10,
  width: 70,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: 8,
  pointerEvents: 'none',
});

/** One icon has the whole corner; two share it, and stop short of the notes. */
const Intent = styled('span')({
  display: 'block',
  '& > svg': { width: '100%', height: '100%' },
});

/** Which list holds the event: drawn over the intent icons, deliberately. */
const Mark = styled('div')({
  position: 'absolute',
  top: 27,
  right: 29.5,
  display: 'flex',
  transform: 'translate(50%, -50%)',
  pointerEvents: 'none',
});

/** The icon's check is cut out of its circle; this fills it, so it reads over an intent icon. */
const OnSchedule = styled('span')({
  position: 'relative',
  display: 'block',
  color: SCHEDULED_MARK,
  '&::before': {
    content: '""',
    position: 'absolute',
    inset: 5,
    borderRadius: '50%',
    background: BRAND.parchment,
  },
  '& > svg': { position: 'relative' },
});

const Notes = styled('div')({
  position: 'absolute',
  top: NOTES_TOP,
  right: -NOTE_W / 2,
  display: 'flex',
  flexDirection: 'column',
  gap: NOTE_GAP,
});

/** The left half sits on the ticket at half strength; the right half is the tag's own colour. */
const Note = styled('span')({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  boxSizing: 'border-box',
  width: NOTE_W,
  height: NOTE_H,
  paddingLeft: NOTE_W / 2,
  borderRadius: '0 4px 4px 0',
  color: BRAND.mocha,
});

/** Beside the time: "Past event" or "Not Interested", when those toggles list them. */
const StateNote = styled('span')({
  fontSize: 9.5,
  fontWeight: 700,
  lineHeight: 1.4,
  letterSpacing: '0.08em',
  textTransform: 'uppercase',
  padding: '1px 7px',
  borderRadius: 999,
  background: 'rgba(54,47,42,0.12)',
  whiteSpace: 'nowrap',
});

/** Cancelled, Full, the age warning and "+ N more dates", under the facts. */
const ChipLine = styled('div')({
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'center',
  gap: 5,
  marginTop: 4,
  paddingRight: TEXT_CLEAR,
});

const MoreDates = styled('span')({
  fontFamily: FONTS.body,
  fontSize: 11.5,
  fontWeight: 700,
  color: 'rgba(54,47,42,0.75)',
});

export function PinGlyph() {
  return (
    <svg width={11} height={13} viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path
        fill="currentColor"
        d="M12 2a7 7 0 0 0-7 7c0 5 7 13 7 13s7-8 7-13a7 7 0 0 0-7-7zm0 9.5A2.5 2.5 0 1 1 12 6a2.5 2.5 0 0 1 0 5.5z"
      />
    </svg>
  );
}

/* ── The ticket ─────────────────────────────────────────────────────────── */

interface Props {
  event: Opportunity;
  /** On Save for Later: the ticket's paper darkens and the corner says which list. */
  saved?: boolean;
  scheduled?: boolean;
  past?: boolean;
  hidden?: boolean;
  /**
   * The title as a link to the listing. The app's card has none — the whole
   * ticket opens the callout — but a shared schedule has nowhere else to send
   * a reader who wants the event itself.
   */
  titleHref?: string;
  /** Drawn at the ticket's foot, clear of the notes. */
  action?: ReactNode;
  /** Put on the ticket, for the list to find it again. */
  id?: string;
  /** Under 18 is on and the listing states no age. */
  ageWarn?: boolean;
  /** Later dates of the same series that also match. */
  moreDates?: number;
  /** Travel time from the origin, after the neighborhood. */
  travel?: ReactNode;
}

export default function EventTicket({
  event,
  saved = false,
  scheduled = false,
  past = false,
  hidden = false,
  titleHref,
  action,
  id,
  ageWarn = false,
  moreDates = 0,
  travel,
}: Props) {
  const badges = accessBadges(event);
  const intents = event.roles.slice(0, MAX_INTENTS);
  const notes = event.topics.slice(0, MAX_NOTES);
  const intentBox = intents.length > 1 ? { width: 46, height: 40 } : { width: 70, height: 61 };
  const slot = timeSlotOf(event);

  return (
    <Frame>
      <Card
        $saved={saved}
        data-event-id={event.id}
        style={
          notes.length
            ? { minHeight: NOTES_TOP + notes.length * (NOTE_H + NOTE_GAP) - NOTE_GAP + NOTES_FOOT }
            : undefined
        }
      >
        <Title id={id}>
          {titleHref ? (
            <TitleLink href={titleHref} target="_blank" rel="noopener noreferrer">
              {event.name}
            </TitleLink>
          ) : (
            event.name
          )}
        </Title>

        <Facts>
          <FactIcon>{slot && <Icon name={`time:${slot}`} size={14} />}</FactIcon>
          <FactText>
            <span>
              {shortDate(localDate(event))} · {formatTimeRange(event)}
            </span>
            {past && <StateNote>Past event</StateNote>}
            {hidden && <StateNote>Not Interested</StateNote>}
          </FactText>
          <FactIcon>
            <PinGlyph />
          </FactIcon>
          {event.neighborhood ? (
            <FactText>
              <span>{formatArea(event.neighborhood)}</span>
              {travel}
            </FactText>
          ) : (
            <FactText $plain>
              <span>{venueText(event)}</span>
              {travel}
            </FactText>
          )}
        </Facts>

        {(event.status === 'cancelled' || event.capacityStatus === 'full' || ageWarn || moreDates > 0) && (
          <ChipLine>
            {event.status === 'cancelled' && <Chip data-kind="cancelled">Cancelled</Chip>}
            {event.status !== 'cancelled' && event.capacityStatus === 'full' && <Chip data-kind="full">Full</Chip>}
            {ageWarn && <Chip data-kind="age">Age not stated</Chip>}
            {moreDates > 0 && <MoreDates>{moreDatesLabel(moreDates)}</MoreDates>}
          </ChipLine>
        )}

        {event.summary && <Summary>{event.summary}</Summary>}

        {badges.length > 0 && (
          <Badges style={{ marginTop: 8, paddingRight: TEXT_CLEAR }}>
            {badges.map((b) => (
              <Badge key={b}>{b}</Badge>
            ))}
          </Badges>
        )}

        {action && <Action>{action}</Action>}

        {intents.length > 0 && (
          <Intents style={{ color: saved ? INTENT_ON_SAVED : INTENT_COLOR }}>
            {intents.map((role) => (
              <Intent key={role.key} role="img" aria-label={role.label} style={intentBox}>
                <Icon name={`role:${role.key}`} emoji={role.icon} size="100%" />
              </Intent>
            ))}
          </Intents>
        )}

        {scheduled ? (
          <Mark role="img" aria-label="Going">
            <OnSchedule>
              <Icon name="ui:scheduleOn" size={25} />
            </OnSchedule>
          </Mark>
        ) : saved ? (
          <Mark role="img" aria-label="Saved for later" style={{ color: SAVED_MARK }}>
            <Icon name="ui:saveOn" size={25} />
          </Mark>
        ) : null}
      </Card>

      {notes.length > 0 && (
        <Notes style={saved ? { opacity: 0.5 } : undefined}>
          {notes.map((topic) => {
            const color = TAG_COLORS[`topic:${topic.key}`] ?? BRAND.muted;
            return (
              <Note
                key={topic.key}
                role="img"
                aria-label={topic.label}
                style={{ background: `linear-gradient(to right, ${withAlpha(color, 0.5)} 50%, ${color} 50%)` }}
              >
                <Icon name={`topic:${topic.key}`} emoji={topic.icon} size={17} />
              </Note>
            );
          })}
        </Notes>
      )}
    </Frame>
  );
}
